import { inject, signal, Service } from '@angular/core';
import { openDB } from 'idb';
import { ChecklistHttpService } from '../checklist-http.service';
import { environment } from '../../../environments/environment';
import { AppToastService } from '../app-toast.service';

export interface PendingAction {
  actionType: keyof ChecklistHttpService;
  params: unknown[];
  timestamp?: number;
}

export type QueuedUploadRemovalResult = 'removed' | 'in-flight' | 'not-found';

interface PendingBlobRecord {
  fileId: string;
  dataBase64: string;
  contentType: string;
  uploadConfirmed: boolean;
  updatedAt: number;
}

interface UploadActionMetadata {
  fileName: string;
  contentType: string;
}

@Service()
export class OfflineStorageService {
  private readonly checklistHttpService = inject(ChecklistHttpService);
  private readonly appToastService = inject(AppToastService);
  private readonly dbName = 'offline-queue';
  private readonly dbVersion = 2;
  private readonly actionsStore = 'actions';
  private readonly pendingBlobsStore = 'pending-blobs';
  private readonly swSyncTag = 'sync-offline-actions';
  private dbPromise: ReturnType<typeof openDB> | null = null;
  private pendingActionsSignal = signal<PendingAction[]>([]);
  private storageUsageRatioSignal = signal(0);
  private readonly inFlightUploadFileIds = new Set<string>();
  private pendingActionsRefreshVersion = 0;
  private readonly startupPromise: Promise<void>;

  pendingActions = this.pendingActionsSignal.asReadonly();
  storageUsageRatio = this.storageUsageRatioSignal.asReadonly();

  constructor() {
    this.startupPromise = this.initializeStorage().catch(error => {
      console.error('Offline storage initialization failed:', error);
    });
  }

  private async initializeStorage(): Promise<void> {
    await this.initDB();
    await this.refreshPendingActions();
    await this.refreshStorageEstimate();
    this.showStorageQuotaWarningIfNeeded();
  }

  private async initDB() {
    await this.getDB();
  }

  private getDB() {
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = openDB(this.dbName, this.dbVersion, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('actions')) {
          db.createObjectStore('actions', {
            keyPath: 'id',
            autoIncrement: true,
          });
        }
        if (!db.objectStoreNames.contains('pending-blobs')) {
          db.createObjectStore('pending-blobs', {
            keyPath: 'fileId',
          });
        }
      },
    });
    this.dbPromise.catch(() => {
      this.dbPromise = null;
    });
    return this.dbPromise;
  }

  async addAction<T extends keyof ChecklistHttpService>(
    method: ChecklistHttpService[T],
    ...params: Parameters<ChecklistHttpService[T]>
  ) {
    const actionType = Object.entries(ChecklistHttpService.methodMap).find(
      ([, methodName]) => this.checklistHttpService[methodName] === method
    )?.[1] as T;

    if (!actionType) {
      throw new Error('Function not found in ChecklistHttpService');
    }

    const db = await this.getDB();
    const storedParams = this.serializeActionParams(actionType, params);
    const action = { actionType, params: storedParams, timestamp: Date.now() };

    await db.add(this.actionsStore, action);

    await this.refreshPendingActions();

    await this.registerBackgroundSync();
  }

  private serializeActionParams<T extends keyof ChecklistHttpService>(
    actionType: T,
    params: Parameters<ChecklistHttpService[T]>
  ): unknown[] {
    if (actionType !== 'uploadFile') {
      return [...params];
    }

    const [checklistId, itemId, customInputId, fileId, file] =
      params as Parameters<ChecklistHttpService['uploadFile']>;
    const metadata: UploadActionMetadata = {
      fileName: file.name,
      contentType: file.type,
    };
    return [checklistId, itemId, customInputId, fileId, metadata];
  }

  async refreshPendingActions(): Promise<void> {
    const refreshVersion = ++this.pendingActionsRefreshVersion;
    const db = await this.getDB();
    const actions = await db.getAll(this.actionsStore);
    if (refreshVersion !== this.pendingActionsRefreshVersion) {
      return;
    }
    this.pendingActionsSignal.set(
      actions.map(({ actionType, params }) => ({ actionType, params }))
    );
  }

  async refreshStorageEstimate(): Promise<void> {
    const storage =
      typeof navigator === 'undefined' ? undefined : navigator.storage;
    if (!storage || typeof storage.estimate !== 'function') {
      this.storageUsageRatioSignal.set(0);
      return;
    }

    try {
      const estimate = await storage.estimate();
      const usage = estimate.usage ?? 0;
      const quota = estimate.quota ?? 0;
      const ratio = quota > 0 ? usage / quota : 0;
      this.storageUsageRatioSignal.set(Number.isFinite(ratio) ? ratio : 0);
    } catch {
      this.storageUsageRatioSignal.set(0);
    }
  }

  showStorageQuotaWarningIfNeeded(): boolean {
    if (this.storageUsageRatio() <= 0.8) {
      return false;
    }

    this.appToastService.showImageUploadQuotaWarning(this.queuedUploadCount());
    return true;
  }

  queuedUploadCount(): number {
    return this.pendingActions().filter(
      action => action.actionType === 'uploadFile'
    ).length;
  }

  private async loadPendingActions() {
    await this.refreshPendingActions();
  }

  async removeFirstAction() {
    const db = await this.getDB();
    const tx = db.transaction(this.actionsStore, 'readwrite');
    const store = tx.objectStore(this.actionsStore);
    const firstAction = await store.openCursor();

    if (firstAction) {
      await store.delete(firstAction.key);
      await tx.done;

      await this.refreshPendingActions();
    }
  }

  async removeFirstActionIfMatches(action: PendingAction): Promise<boolean> {
    const db = await this.getDB();
    const tx = db.transaction(this.actionsStore, 'readwrite');
    const store = tx.objectStore(this.actionsStore);
    const firstAction = await store.openCursor();

    if (!firstAction) {
      await tx.done;
      return false;
    }

    if (!this.isSameAction(firstAction.value as PendingAction, action)) {
      await tx.done;
      await this.refreshPendingActions();
      return false;
    }

    await store.delete(firstAction.key);
    await tx.done;
    await this.refreshPendingActions();
    return true;
  }

  async isFirstAction(action: PendingAction): Promise<boolean> {
    const db = await this.getDB();
    const tx = db.transaction(this.actionsStore, 'readonly');
    const store = tx.objectStore(this.actionsStore);
    const firstAction = await store.openCursor();
    const matches =
      !!firstAction &&
      this.isSameAction(firstAction.value as PendingAction, action);
    await tx.done;
    return matches;
  }

  async removeQueuedUploadByFileId(
    fileId: string
  ): Promise<QueuedUploadRemovalResult> {
    if (this.inFlightUploadFileIds.has(fileId)) {
      return 'in-flight';
    }

    const db = await this.getDB();
    const tx = db.transaction(this.actionsStore, 'readwrite');
    const store = tx.objectStore(this.actionsStore);
    let cursor = await store.openCursor();
    let removed = false;

    while (cursor) {
      const action = cursor.value as PendingAction;
      if (
        action.actionType === 'uploadFile' &&
        typeof action.params[3] === 'string' &&
        action.params[3] === fileId
      ) {
        await cursor.delete();
        removed = true;
        break;
      }
      cursor = await cursor.continue();
    }

    await tx.done;
    if (removed) {
      await this.refreshPendingActions();
    }
    return removed ? 'removed' : 'not-found';
  }

  async markQueuedDeleteUploadRejected(fileId: string): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction(this.actionsStore, 'readwrite');
    const store = tx.objectStore(this.actionsStore);
    let cursor = await store.openCursor();
    let updated = false;

    while (cursor) {
      const action = cursor.value as PendingAction;
      if (
        action.actionType === 'deleteFile' &&
        typeof action.params[2] === 'string' &&
        action.params[2] === fileId &&
        typeof action.params[3] === 'object' &&
        action.params[3] !== null
      ) {
        action.params = [
          ...action.params.slice(0, 3),
          {
            ...(action.params[3] as Record<string, unknown>),
            uploadRejected: true,
          },
          ...action.params.slice(4),
        ];
        await cursor.update(action);
        updated = true;
      }
      cursor = await cursor.continue();
    }

    await tx.done;
    if (updated) {
      await this.refreshPendingActions();
    }
  }

  markActionInFlight(action: PendingAction): void {
    const fileId = this.getUploadFileId(action);
    if (fileId) {
      this.inFlightUploadFileIds.add(fileId);
    }
  }

  clearActionInFlight(action: PendingAction): void {
    const fileId = this.getUploadFileId(action);
    if (fileId) {
      this.inFlightUploadFileIds.delete(fileId);
    }
  }

  async putBlob(fileId: string, blob: Blob): Promise<void> {
    const db = await this.getDB();
    const record: PendingBlobRecord = {
      fileId,
      dataBase64: await this.readBlobDataBase64(blob),
      contentType: blob.type,
      uploadConfirmed: false,
      updatedAt: Date.now(),
    };
    await db.put(this.pendingBlobsStore, record);
    await this.refreshStorageEstimate();
  }

  async getBlob(fileId: string): Promise<Blob | null> {
    const record = await this.getPendingBlobRecord(fileId);
    if (!record) {
      return null;
    }

    return new Blob([this.base64ToArrayBuffer(record.dataBase64)], {
      type: record.contentType,
    });
  }

  async isBlobUploadConfirmed(fileId: string): Promise<boolean> {
    const record = await this.getPendingBlobRecord(fileId);
    return record?.uploadConfirmed === true;
  }

  private arrayBufferToBase64(data: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(data);
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }
    return btoa(binary);
  }

  private base64ToArrayBuffer(data: string): ArrayBuffer {
    const binary = atob(data);
    const buffer = new ArrayBuffer(binary.length);
    const bytes = new Uint8Array(buffer);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return buffer;
  }

  private readBlobDataBase64(blob: Blob): Promise<string> {
    if (typeof FileReader !== 'undefined') {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            const separatorIndex = reader.result.indexOf(',');
            resolve(
              separatorIndex >= 0
                ? reader.result.slice(separatorIndex + 1)
                : reader.result
            );
          } else {
            reject(new Error('Failed to read blob data'));
          }
        };
        reader.onerror = () =>
          reject(reader.error ?? new Error('Failed to read blob data'));
        reader.readAsDataURL(blob);
      });
    }

    const data =
      typeof blob.arrayBuffer === 'function'
        ? blob.arrayBuffer()
        : new Response(blob).arrayBuffer();
    return data.then(buffer => this.arrayBufferToBase64(buffer));
  }

  async markUploadConfirmed(fileId: string): Promise<void> {
    const db = await this.getDB();
    const record = await this.getPendingBlobRecord(fileId);
    if (!record) {
      return;
    }

    await db.put(this.pendingBlobsStore, {
      ...record,
      uploadConfirmed: true,
      updatedAt: Date.now(),
    });
  }

  async evictBlob(fileId: string): Promise<void> {
    const db = await this.getDB();
    await db.delete(this.pendingBlobsStore, fileId);
  }

  isUploadQueued(fileId: string): boolean {
    return this.pendingActions().some(
      action =>
        action.actionType === 'uploadFile' &&
        typeof action.params[3] === 'string' &&
        action.params[3] === fileId
    );
  }

  private isSameAction(first: PendingAction, action: PendingAction): boolean {
    return (
      first.actionType === action.actionType &&
      this.areActionParamsEqual(first.params, action.params)
    );
  }

  private areActionParamsEqual(first: unknown[], second: unknown[]): boolean {
    if (first.length !== second.length) {
      return false;
    }

    return first.every((value, index) =>
      this.areActionParamValuesEqual(value, second[index])
    );
  }

  private areActionParamValuesEqual(first: unknown, second: unknown): boolean {
    if (Object.is(first, second)) {
      return true;
    }

    if (first instanceof File || first instanceof Blob) {
      return false;
    }
    if (second instanceof File || second instanceof Blob) {
      return false;
    }

    try {
      return JSON.stringify(first) === JSON.stringify(second);
    } catch {
      return false;
    }
  }

  private getUploadFileId(action: PendingAction): string | null {
    return action.actionType === 'uploadFile' &&
      typeof action.params[3] === 'string'
      ? action.params[3]
      : null;
  }

  private async getPendingBlobRecord(
    fileId: string
  ): Promise<PendingBlobRecord | undefined> {
    const db = await this.getDB();
    return (await db.get(this.pendingBlobsStore, fileId)) as
      | PendingBlobRecord
      | undefined;
  }

  private async registerBackgroundSync(): Promise<void> {
    if (environment.serviceWorkerEnabled && 'serviceWorker' in navigator) {
      try {
        const registration = (await navigator.serviceWorker
          .ready) as ServiceWorkerRegistration;
        if ('sync' in registration) {
          const syncManager = registration.sync as SyncManager;
          const tags = await syncManager.getTags();
          if (!tags.includes(this.swSyncTag)) {
            await (registration.sync as SyncManager).register(this.swSyncTag);
            console.log('Background Sync registered');
          }
        } else {
          console.log('Background Sync not supported');
        }
      } catch (error) {
        console.error('Background Sync registration failed:', error);
      }
    }
  }
}
